import type { PlatformLocation } from '@angular/common';

import { DEFAULT_BASE_HREF } from './utils/global-constants';

/**
 * Dynamic BaseHref getter for routing
 *
 * Background: within our app, BaseHref should change dynamically depending on
 * the respective environment.
 *
 * e.g.:
 * - within oneweb: `/passengercars/mercedes-benz-cars/car-configurator.html/`
 * - within standalone: `/owcc/` (see DEFAULT_BASE_HREF)
 *
 * OneWeb's AEM URL interpretation only cares about the pathname until it hits
 * the last ".html", so we can take advantage of this to find out our BaseHref
 * within AEM/OneWeb context.
 *
 * If we are not within OneWeb/AEM, we try to get the BaseHref from the Document
 * (<base href="..."> in <head>) using platformLocation.getBaseHrefFromDOM()
 */
export function getBaseHref(platformLocation: PlatformLocation, win: Window): string {
  // every oneweb/aem url contains ".html" in the pathname.
  // standalone/local urls don't.
  // result of this is, that oneweb urls will have a respective base href like:
  // `/passengercars/mercedes-benz-cars/car-configurator.html/`
  // and standalone urls will have a base href like `/owcc/`
  const divider = '.html';
  const pathname = win.location?.pathname;
  const dividerPosition = pathname ? pathname.lastIndexOf(divider) : -1;
  const isOneweb = dividerPosition >= 0;
  const baseHref = isOneweb
    ? pathname.slice(0, Math.max(0, dividerPosition + divider.length))
    : platformLocation.getBaseHrefFromDOM();
  return baseHref || DEFAULT_BASE_HREF;
}
